import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraAudiencePicker, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraInput, SpecraTag, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraDateField, SpecraFormField, SpecraOverlayFooter, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { deliveryClient, projectDataClient } from '../../../lib/connect/clients';
import { eventDescription, useEvents, useSetEventLink } from '../../Capture/eventHooks';
import { useHearsayAudienceDirectory } from './audienceDirectory';
import { useCreateIntelClaim } from './hooks';
const MAX_STATEMENT = 2000;
const ONLY_ME = {
    everyone: false,
    targets: []
};
const MIN_SEARCH = 2;
const sameAudience = (a, b)=>a.everyone === b.everyone && a.targets.length === b.targets.length && a.targets.every((t, i)=>t.kind === b.targets[i]?.kind && t.id === b.targets[i]?.id);
function useDebounced(value, ms = 250) {
    const [v, setV] = useState(value);
    useEffect(()=>{
        const t = setTimeout(()=>setV(value), ms);
        return ()=>clearTimeout(t);
    }, [
        value,
        ms
    ]);
    return v;
}
/** Field error under a picker that renders its own label (SpecraCombobox). */ function FieldError({ message }) {
    if (!message) return null;
    return /*#__PURE__*/ _jsx(SpecraText, {
        as: "p",
        size: "meta",
        tone: "danger",
        role: "alert",
        children: message
    });
}
/**
 * "Add hearsay" (REG-03). Statement, kind, source, who said it, where and
 * when, confidence, expiry, the document for Source = Document, what it is
 * relevant to and "Who can see this" (Only me by default). Validation runs
 * before any request; the server re-checks hearsay.write on save.
 */ export function AddHearsayDialog(props) {
    // Callers keep the dialog mounted and toggle `open`; every new open session
    // starts from a fresh draft (and the current default links/audience).
    const [session, setSession] = useState(0);
    const wasOpen = useRef(props.open);
    useEffect(()=>{
        if (props.open && !wasOpen.current) setSession((n)=>n + 1);
        wasOpen.current = props.open;
    }, [
        props.open
    ]);
    return /*#__PURE__*/ _jsx(AddHearsayDraft, {
        ...props
    }, session);
}
function AddHearsayDraft({ open, onOpenChange, defaultLinks, defaultAudience, contextOptions, canCreateKinds, onCreated }) {
    const { user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    const ids = {
        statement: useId(),
        venue: useId()
    };
    const [statement, setStatement] = useState('');
    const [kind, setKind] = useState('INTEL');
    const [source, setSource] = useState('HEARSAY');
    const [heardFromId, setHeardFromId] = useState();
    const [heardFromText, setHeardFromText] = useState('');
    const [contactQuery, setContactQuery] = useState('');
    const [venue, setVenue] = useState('');
    const [heardAt, setHeardAt] = useState();
    const [eventQuery, setEventQuery] = useState('');
    const events = useEvents({
        search: useDebounced(eventQuery),
        enabled: open
    });
    const heardAtOptions = [
        ...(events.data ?? []).map((event)=>({
                value: `EVENT:${event.id}`,
                label: event.title,
                description: eventDescription(event)
            })),
        ...(contextOptions?.meetings ?? []).map((meeting)=>({
                value: `MEETING:${meeting.id}`,
                label: meeting.label,
                description: 'Pursuit touchpoint'
            }))
    ];
    const [heardOn, setHeardOn] = useState('');
    const [confidence, setConfidence] = useState('');
    const [expiresOn, setExpiresOn] = useState('');
    const [documentId, setDocumentId] = useState();
    const [docQuery, setDocQuery] = useState('');
    const [links, setLinks] = useState(defaultLinks);
    const [audience, setAudience] = useState(defaultAudience ?? ONLY_ME);
    const [errors, setErrors] = useState({});
    const directory = useHearsayAudienceDirectory(open);
    const create = useCreateIntelClaim();
    const setEventLink = useSetEventLink();
    const contactSearch = useDebounced(contactQuery);
    const contacts = useQuery({
        queryKey: [
            'hearsay-heard-from',
            tenantId,
            contactSearch
        ],
        enabled: open && !!tenantId && contactSearch.trim().length >= MIN_SEARCH,
        queryFn: async ()=>(await deliveryClient.listContacts({
                companyId: tenantId,
                search: contactSearch.trim()
            })).contacts
    });
    const docSearch = useDebounced(docQuery);
    const docs = useQuery({
        queryKey: [
            'hearsay-document',
            docSearch
        ],
        enabled: open && source === 'DOCUMENT' && docSearch.trim().length >= MIN_SEARCH,
        queryFn: async ()=>(await projectDataClient.searchLibraryItems({
                filter: {
                    scope: '',
                    query: docSearch.trim()
                },
                offset: 0,
                limit: 10
            })).items
    });
    const allowed = (k, s)=>!canCreateKinds || canCreateKinds.includes(`${k}/${s}`);
    const validate = ()=>{
        const e = {};
        // The server lists the kind/source pairs this viewer may create; never send another.
        if (!allowed(kind, source)) e.pair = "You can't add this kind of entry from this source.";
        if (!statement.trim()) e.statement = 'Write what was said';
        if (source === 'HEARSAY' && !heardFromId && !heardFromText.trim()) e.heardFrom = 'Say who you heard it from';
        if (!confidence) e.confidence = 'Pick a confidence';
        if (source === 'DOCUMENT' && !documentId) e.document = 'Pick the document';
        return e;
    };
    const save = ()=>{
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length > 0) return;
        create.mutate({
            kind,
            source,
            statement: statement.trim(),
            heardFromContactId: heardFromId ?? '',
            heardFromText: heardFromId ? '' : heardFromText.trim(),
            venue: venue.trim(),
            heardOn,
            confidence: confidence,
            expiresOn,
            documentArtifactId: source === 'DOCUMENT' ? documentId ?? '' : '',
            links: [
                ...links,
                ...heardAt ? [
                    heardAt
                ] : []
            ],
            // Only me is the server default; send the audience only when changed.
            audience: sameAudience(audience, ONLY_ME) ? undefined : audience
        }, {
            onSuccess: (claim)=>{
                if (heardAt?.targetKind === 'EVENT' && contextOptions?.pursuitId) {
                    // Best-effort relevance: the successful claim stands even if linking is denied.
                    setEventLink.mutate({
                        eventId: heardAt.targetId,
                        linkRole: 'RELEVANT_TO',
                        targetKind: 'PURSUIT',
                        targetId: contextOptions.pursuitId,
                        source: 'hearsay'
                    });
                }
                onCreated?.(claim);
                onOpenChange(false);
            }
        });
    };
    return /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        size: "md",
        title: "Add hearsay",
        scrollBody: true,
        testId: Testids.registryHearsay.dialog,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-[var(--space-4)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "What was said",
                        htmlFor: ids.statement,
                        required: true,
                        error: errors.statement,
                        hint: `${statement.length}/${MAX_STATEMENT}`,
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: ids.statement,
                            value: statement,
                            maxLength: MAX_STATEMENT,
                            onChange: (e)=>setStatement(e.target.value),
                            "data-testid": Testids.registryHearsay.statement
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "Kind",
                        value: kind,
                        onValueChange: setKind,
                        options: [
                            {
                                value: 'INTEL',
                                label: 'Intel',
                                disabled: !allowed('INTEL', source)
                            },
                            {
                                value: 'HANDLING_NOTE',
                                label: 'Handling note',
                                disabled: !allowed('HANDLING_NOTE', source)
                            }
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "Source",
                        value: source,
                        onValueChange: setSource,
                        options: [
                            {
                                value: 'HEARSAY',
                                label: 'Hearsay',
                                testId: Testids.registryHearsay.sourceHearsay,
                                disabled: !allowed(kind, 'HEARSAY')
                            },
                            {
                                value: 'ANALYST_RESEARCH',
                                label: 'Analyst research',
                                testId: Testids.registryHearsay.sourceAnalyst,
                                disabled: !allowed(kind, 'ANALYST_RESEARCH')
                            },
                            {
                                value: 'DOCUMENT',
                                label: 'Document',
                                testId: Testids.registryHearsay.sourceDocument,
                                disabled: !allowed(kind, 'DOCUMENT')
                            }
                        ]
                    }),
                    /*#__PURE__*/ _jsx(FieldError, {
                        message: errors.pair
                    }),
                    /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Heard from",
                        placeholder: "Who said it",
                        searchPlaceholder: "Search contacts",
                        emptyMessage: contactSearch.trim().length < MIN_SEARCH ? 'Type a name' : 'No matching contacts',
                        loading: contacts.isFetching,
                        value: heardFromId,
                        onValueChange: (v)=>{
                            setHeardFromId(v);
                            if (v) setHeardFromText('');
                        },
                        onSearchValueChange: setContactQuery,
                        shouldFilter: false,
                        options: (contacts.data ?? []).map((c)=>({
                                value: c.id,
                                label: [
                                    c.firstName,
                                    c.lastName
                                ].filter(Boolean).join(' '),
                                description: c.title || undefined
                            })),
                        onCreate: (q)=>{
                            setHeardFromId(undefined);
                            setHeardFromText(q.trim());
                        },
                        createLabel: (q)=>`Use “${q}” (not in the registry)`,
                        fallbackLabel: heardFromText || undefined,
                        clearable: true
                    }),
                    /*#__PURE__*/ _jsx(FieldError, {
                        message: errors.heardFrom
                    }),
                    /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Heard at",
                        placeholder: "Choose an event or touchpoint",
                        searchPlaceholder: "Search events by title",
                        options: heardAtOptions,
                        shouldFilter: false,
                        loading: events.isFetching,
                        emptyMessage: events.isError ? 'Events could not be loaded' : 'No matching events',
                        onSearchValueChange: setEventQuery,
                        value: heardAt ? `${heardAt.targetKind}:${heardAt.targetId}` : undefined,
                        selectedOption: heardAt ? {
                            value: `${heardAt.targetKind}:${heardAt.targetId}`,
                            label: heardAt.label
                        } : undefined,
                        onValueChange: (value)=>{
                            const option = heardAtOptions.find((o)=>o.value === value);
                            const [targetKind, targetId] = (value ?? '').split(':');
                            setHeardAt(option ? {
                                linkRole: 'HEARD_AT',
                                targetKind,
                                targetId,
                                label: option.label
                            } : undefined);
                        },
                        clearable: true,
                        triggerProps: {
                            'data-testid': Testids.registryHearsay.heardAt
                        }
                    }),
                    events.isError && /*#__PURE__*/ _jsx(FieldError, {
                        message: "Events could not be loaded. Try again; pursuit touchpoints are still available."
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Where",
                        htmlFor: ids.venue,
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            id: ids.venue,
                            value: venue,
                            placeholder: "Where (free text)",
                            onChange: (e)=>setVenue(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraDateField, {
                        label: "When",
                        value: heardOn,
                        onValueChange: setHeardOn,
                        clearable: true
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Confidence",
                        required: true,
                        error: errors.confidence,
                        children: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                            label: "Confidence",
                            value: confidence,
                            onValueChange: setConfidence,
                            options: [
                                {
                                    value: 'LOW',
                                    label: 'Low'
                                },
                                {
                                    value: 'MEDIUM',
                                    label: 'Medium',
                                    testId: Testids.registryHearsay.confidenceMedium
                                },
                                {
                                    value: 'HIGH',
                                    label: 'High'
                                }
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraDateField, {
                        label: "Expires",
                        value: expiresOn,
                        onValueChange: setExpiresOn,
                        clearable: true
                    }),
                    source === 'DOCUMENT' && /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Document",
                        placeholder: "Pick a library document",
                        searchPlaceholder: "Search the library",
                        emptyMessage: docSearch.trim().length < MIN_SEARCH ? 'Type a title' : 'No documents',
                        loading: docs.isFetching,
                        value: documentId,
                        onValueChange: setDocumentId,
                        onSearchValueChange: setDocQuery,
                        shouldFilter: false,
                        options: (docs.data ?? []).map((d)=>({
                                value: d.id,
                                label: d.title
                            }))
                    }),
                    source === 'DOCUMENT' && /*#__PURE__*/ _jsx(FieldError, {
                        message: errors.document
                    }),
                    links.length > 0 && /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Relevant to",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "flex flex-wrap gap-[var(--space-1-5)]",
                            children: links.map((l)=>/*#__PURE__*/ _jsx(SpecraTag, {
                                    onRemove: l.locked ? undefined : ()=>setLinks((prev)=>prev.filter((x)=>x !== l)),
                                    removeLabel: `Remove ${l.label}`,
                                    children: l.label
                                }, `${l.linkRole}:${l.targetKind}:${l.targetId}`))
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Who can see this",
                        hint: "Only you until you share it. You can change this any time.",
                        "data-testid": Testids.registryHearsay.audienceField,
                        children: /*#__PURE__*/ _jsx(SpecraAudiencePicker, {
                            mode: "inline",
                            value: audience,
                            onChange: setAudience,
                            ...directory
                        })
                    }),
                    create.isError && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "body-ui",
                        tone: "danger",
                        role: "alert",
                        children: create.error instanceof Error ? create.error.message : 'Could not save hearsay.'
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                confirmLabel: "Save hearsay",
                onConfirm: save,
                onCancel: ()=>onOpenChange(false),
                loading: create.isPending,
                confirmProps: {
                    'data-testid': Testids.registryHearsay.save
                }
            })
        ]
    });
}
